import styled, { css } from 'styled-components';

type RiskColor = 'green' | 'yellow' | 'red' | 'text';

const COLORS: Record<RiskColor, string> = {
  green: '#28ec99',
  yellow: '#fcff57',
  red: '#ff5858',
  text: '#BECCF6',
};

export const Root = styled.div`
  width: 100%;
  padding-bottom: 16px;
`;

export const Token = styled.div`
  margin-bottom: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
`;

export const TokenInfo = styled.div`
  margin-left: 10px;
  overflow: auto;
`;

export const TokenName = styled.div`
  margin-bottom: 2px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const TokenAmount = styled.div`
  font-family: 'Share Tech Mono', monospace;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const Risk = styled.div`
  margin: 0 -16px 12px;
  width: calc(100% + 32px);
  padding: 10px 16px;
  background: rgba(36, 47, 104, 0.5);
`;

export const RiskMain = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

export const RiskLeft = styled.div`
  flex-basis: 140px;
  margin-right: 16px;
  display: flex;
  justify-content: center;
`;

export const RiskLeftContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const RiskValue = styled.div<{ $color: RiskColor }>`
  margin-bottom: 7px;
  align-self: center;
  font-family: 'Share Tech Mono', monospace;
  font-size: 19px;
  font-weight: bold;
  color: ${({ $color }) => COLORS[$color]};
  white-space: nowrap;
`;

export const Bar = styled.div<{ $percent: number; $color: RiskColor }>`
  margin-bottom: 10px;
  height: 6px;
  width: 100%;
  border-radius: 4px;
  background: linear-gradient(
    to right,
    ${COLORS.green} 0%,
    ${COLORS.green} 13%,
    ${COLORS.yellow} 33%,
    ${COLORS.yellow} 43%,
    ${COLORS.red} 63%,
    ${COLORS.red} 100%
  );
  position: relative;

  &:after {
    content: '▲';
    position: absolute;
    top: 2px;
    left: ${({ $percent }) => $percent}%;
    transform: translateX(-50%);
    font-size: 16px;
    color: ${({ $color }) => COLORS[$color]};
  }
`;

export const RiskRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

export const RiskLogo = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;

  span {
    font-family: 'Share Tech Mono', monospace;
    font-style: italic;
    color: rgba(255, 255, 255, 0.4);
    font-size: 10px;
  }

  a {
    height: 18px;
    display: flex;
    align-items: center;
    color: rgba(155, 89, 182, 0.8);
    font-family: 'Share Tech Mono', monospace;
    font-size: 10px;
    text-decoration: none;
    &:hover {
      color: rgba(155, 89, 182, 1);
    }
  }
`;

export const RiskButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const IssuesButton = styled.button<{ $isOpen: boolean }>`
  border: none;
  background: rgba(255, 255, 255, 0.12);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 11px;
  color: #fff;
  white-space: nowrap;
  transition: 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      &::after {
        content: '▲';
        font-size: 8px;
      }
    `}

  ${({ $isOpen }) =>
    !$isOpen &&
    css`
      &::after {
        content: '▼';
        font-size: 8px;
      }
    `}
`;

export const RiskLink = styled.a`
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.12);
  font-family: 'Share Tech Mono', monospace;
  font-size: 11px;
  color: #fff;
  text-decoration: none;
  white-space: nowrap;
  transition: 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

export const Issues = styled.div`
  margin-top: 16px;
`;

export const Issue = styled.div`
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(77, 86, 132, 0.5);

  &:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }
`;

export const IssueHeader = styled.div`
  margin-bottom: 6px;
  display: flex;
  align-items: center;
`;

export const IssueCaption = styled.span`
  margin-right: 12px;
  font-family: 'Share Tech Mono', monospace;
  font-weight: bold;
  font-size: 12px;
  color: #fff;
`;

export const IssueSeverity = styled.span`
  font-family: 'Share Tech Mono', monospace;
  color: rgba(255, 255, 255, 0.4);
  font-size: 10px;
`;

export const IssueText = styled.div`
  font-family: 'Share Tech Mono', monospace;
  font-size: 10px;
  color: rgba(232, 237, 254, 0.8);
`;

export const WarningWrapper = styled.div`
  margin-bottom: 12px;
  padding: 10px;
  border-radius: 8px;
  background: rgba(241, 196, 15, 0.1);
  border: 1px solid rgba(241, 196, 15, 0.3);
`;

export const WarningContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const WarningText = styled.div`
  margin-bottom: 10px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 11px;
  color: rgba(241, 196, 15, 0.9);
  text-align: center;
  line-height: 1.4;
`;

export const ScanLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: underline;
  transition: 0.15s;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
`;

export const Text = styled.div`
  width: 100%;
  margin-bottom: 12px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1.5;
`;

export const Accept = styled.div`
  margin-bottom: 12px;
`;

export const ImportButton = styled.button`
  display: block;
  width: 100%;
  padding: 10px;
  background: linear-gradient(135deg, rgba(155, 89, 182, 0.5), rgba(52, 152, 219, 0.5));
  border: none;
  border-radius: 8px;
  color: #fff;
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export const Message = styled.div`
  font-family: 'Share Tech Mono', monospace;
  font-size: 12px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
  padding: 12px 0;
`;
